import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import {
  Agreement,
  AgreementsService,
  AgreementStatus,
  ConsolidatedAgreementsRestService
} from '@tdwb/lazy/platform/preferences';
import {
  BlockDirective,
  ButtonComponent,
  DetailedLoadingIndicatorComponent,
  IconComponent,
  ModalCloseReason,
  ModalPanelComponent,
  ModalService,
  SecurityService,
  ToastMessageService
} from '@tdwb/core';
import { ConsolidatedAgreementsListComponent } from '../common/consolidated-agreements-form/consolidated-agreements-list.component';
import { WebbrokerAgreementsService } from './webbroker-agreements.service';

@Component({
  selector: 'td-wb-unsigned-agreements',
  templateUrl: './unsigned-agreements.component.html',
  styleUrls: ['./unsigned-agreements.component.scss'],
  providers: [AgreementsService, WebbrokerAgreementsService, ConsolidatedAgreementsRestService],
  imports: [
    TranslateDirective,
    IconComponent,
    ModalPanelComponent,
    ConsolidatedAgreementsListComponent,
    ButtonComponent,
    BlockDirective,
    DetailedLoadingIndicatorComponent
  ]
})
export class UnsignedAgreementsComponent {
  protected readonly unsignedAgreements = signal<Array<Agreement>>([]);
  protected readonly loading = signal(true);
  protected readonly isBlocked = signal(false);

  private readonly agreementsService = inject(AgreementsService);
  private readonly webbrokerAgreementsService = inject(WebbrokerAgreementsService);
  private readonly modalService = inject(ModalService);
  private readonly securityService = inject(SecurityService);
  private readonly toastMessageService = inject(ToastMessageService);

  constructor() {
    this.agreementsService.fetchAgreements()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (data: Array<Agreement>): void => {
          this.unsignedAgreements.set(this.getUnsignedAgreements(data));
          this.loading.set(false);
        },
        error: (): void => {
          // Business does not want to block users from using the site when there's an error
          const topModal = this.modalService.getTopModal();
          topModal?.close(ModalCloseReason.HEADER_BUTTON);
          this.loading.set(false);
        }
      });
  }

  submitAgreements(): void {
    this.isBlocked.set(true);
    this.webbrokerAgreementsService.saveAgreementsWithNoClientData(this.unsignedAgreements()).subscribe({
      next: (): void => {
        this.securityService.addUserPermission('ALL_AGREEMENTS_SIGNED');
        this.toastMessageService.setToast('tdWb.platform.unsignedAgreements.TOAST_MESSAGE')
        const topModal = this.modalService.getTopModal();
        topModal?.close(ModalCloseReason.HEADER_BUTTON);
        this.isBlocked.set(false);
      },
      error: (): void => {
        // Business does not want to block users from using the site when there's an error
        const topModal = this.modalService.getTopModal();
        topModal?.close(ModalCloseReason.HEADER_BUTTON);
        this.isBlocked.set(false);
      }
    });
  }

  private getUnsignedAgreements(agreements: Array<Agreement>): Array<Agreement> {
    return agreements?.filter((agreement: Agreement): boolean =>  agreement.agreementStatus === AgreementStatus.UNSIGNED) ?? [];
  }
}
